/**
 * Homepage proof module — real Search Console exports, click to open the story.
 *
 * Replaces the three summary case-study cards. The graphs do the persuading:
 * an unedited GSC curve is harder to dismiss than a claimed figure, so the
 * module leads with the screenshots and sends people to /client-stories for
 * the rest rather than trying to summarise them here.
 *
 * Card layout and the modal mirror the Blogger Outreach page's proof section,
 * and both read GSC_CASES from lib/gscProof so the numbers can't drift.
 */

import React, { useState, useEffect } from "react";
import { GSC_CASES, type GscCase } from "@/lib/gscProof";
import { useIsMobile } from "@/hooks/useIsMobile";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

const glassCyan: React.CSSProperties = {
  background: "rgba(87,215,226,0.04)",
  border: "1px solid rgba(87,215,226,0.16)",
};

function CaseText({ g }: { g: GscCase }) {
  return (
    <div style={{ padding: "28px 30px", display: "flex", flexDirection: "column", gap: "12px", justifyContent: "center" }}>
      <span style={{ color: "rgba(255,255,255,0.42)", fontSize: "13px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.1em" }}>{g.label}</span>
      <h3 style={{ color: "white", fontSize: "24px", fontWeight: 800, fontFamily: ff, margin: 0, letterSpacing: "-0.02em" }}>{g.title}</h3>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "15.5px", fontFamily: ff, lineHeight: 1.65, margin: 0 }}>{g.body}</p>
      <div style={{ display: "flex", gap: "28px", flexWrap: "wrap", marginTop: "6px" }}>
        {([[g.clicks, "clicks"], [g.impressions, "impressions"], [g.position, "avg position"]] as const).map(([v, l]) => (
          <span key={l}>
            <span style={{ display: "block", color: cyan, fontSize: "24px", fontWeight: 800, fontFamily: ff }}>{v}</span>
            <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff }}>{l}</span>
          </span>
        ))}
      </div>
      <span style={{ color: cyan, fontSize: "13.5px", fontWeight: 700, fontFamily: ff }}>See the full story →</span>
    </div>
  );
}

export default function GscProof() {
  const isMobile = useIsMobile();
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const open = openIdx === null ? null : GSC_CASES[openIdx];

  // Lock the page behind the modal and let Escape close it.
  useEffect(() => {
    if (open === null) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenIdx(null); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <section className="section-grid" style={{ background: "#0a0a0a", padding: isMobile ? "60px 20px" : "100px 72px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ marginBottom: isMobile ? "40px" : "56px", maxWidth: "760px" }}>
          <h2 style={{ color: "white", fontSize: isMobile ? "32px" : "48px", fontWeight: 700, fontFamily: ff, margin: "0 0 16px", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
            From flat pipeline{" "}<span style={{ color: cyan }}>to real revenue.</span>
          </h2>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: isMobile ? "16px" : "18px", fontFamily: ff, lineHeight: 1.6, margin: 0 }}>
            On average, clients see the curve turn within three to six months — then it compounds. Open one to see it.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {GSC_CASES.map((g, i) => (
            <button
              key={g.title}
              onClick={() => setOpenIdx(i)}
              aria-haspopup="dialog"
              style={{
                ...glassCyan, borderRadius: "20px", overflow: "hidden", cursor: "pointer",
                textAlign: "left", padding: 0, fontFamily: ff,
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : (i % 2 === 0 ? "1.55fr 1fr" : "1fr 1.55fr"),
              }}
            >
              {(!isMobile && i % 2 === 1) && <CaseText g={g} />}
              <div style={{ background: "#ffffff", padding: "14px", display: "flex", alignItems: "center" }}>
                <img src={g.shot} alt={g.alt} loading="lazy" style={{ width: "100%", display: "block", borderRadius: "8px" }} />
              </div>
              {(isMobile || i % 2 === 0) && <CaseText g={g} />}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
          <a href="/client-stories" className="btn-cyan" style={{ height: "52px", padding: "0 30px", fontSize: "16px" }}>
            See all case studies →
          </a>
        </div>
      </div>

      {/* ── Expanded story ── */}
      {open && (
        <div
          role="dialog" aria-modal="true" aria-label={open.title}
          onClick={() => setOpenIdx(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "16px" : "32px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#0c0e0e", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "18px",
              width: "100%", maxWidth: "760px", maxHeight: "88vh", display: "flex", flexDirection: "column",
              boxShadow: "0 30px 90px rgba(0,0,0,0.7)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", padding: "18px 22px", borderBottom: "1px solid rgba(255,255,255,0.09)", flexShrink: 0 }}>
              <div style={{ minWidth: 0 }}>
                <span style={{ display: "block", color: cyan, fontSize: "12.5px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.1em", marginBottom: "3px" }}>{open.label}</span>
                <h3 style={{ color: "white", fontSize: "18px", fontWeight: 700, fontFamily: ff, margin: 0 }}>{open.title}</h3>
              </div>
              <button
                onClick={() => setOpenIdx(null)} aria-label="Close"
                style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.7)", fontSize: "16px", cursor: "pointer", flexShrink: 0, lineHeight: 1 }}
              >✕</button>
            </div>

            <div style={{ overflowY: "auto", padding: "24px" }}>
              <div style={{ background: "#ffffff", borderRadius: "10px", padding: "14px", marginBottom: "18px" }}>
                <img src={open.shot} alt={open.alt} style={{ width: "100%", display: "block", borderRadius: "6px" }} />
              </div>
              <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "15.5px", fontFamily: ff, lineHeight: 1.7, margin: "0 0 12px" }}>{open.body}</p>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "14.5px", fontFamily: ff, lineHeight: 1.7, margin: "0 0 18px" }}>{open.detail}</p>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "12px" }}>
                {([["Clicks", open.clicks], ["Impressions", open.impressions], ["Avg position", open.position], ["Window", open.window]] as const).map(([l, v]) => (
                  <div key={l} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "12px 14px" }}>
                    <span style={{ display: "block", color: "rgba(255,255,255,0.42)", fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "4px" }}>{l}</span>
                    <span style={{ color: "white", fontSize: "14.5px", fontWeight: 700, fontFamily: ff }}>{v}</span>
                  </div>
                ))}
              </div>
              {open.href && (
                <a href={open.href} className="btn-cyan" style={{ height: "46px", padding: "0 24px", fontSize: "15px", marginTop: "20px" }}>
                  Read the full case study →
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
